import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Fire, FunnelSimple, X, Spinner, CaretRight, Storefront } from "@phosphor-icons/react";
import HomeProductCard from "./HomeProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 12;
export const DEFAULT_BUDGETS = [
  { key: "b1", label: "< $2,500", min: 0, max: 2500 },
  { key: "b2", label: "$2.5–4k", min: 2500, max: 4000 },
  { key: "b3", label: "$4–6k", min: 4000, max: 6000 },
  { key: "b4", label: "$6k+", min: 6000, max: null },
];
const ramGb = (p) => { const m = String(p.specs?.ram || "").match(/(\d+)\s*gb/i); return m ? Number(m[1]) : null; };
const toggleIn = (set, v) => { const n = new Set(set); n.has(v) ? n.delete(v) : n.add(v); return n; };
const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
export const scrollToCatalog = () => document.getElementById("catalogo")?.scrollIntoView({ behavior: "smooth", block: "start" });

// Estado del catálogo del home, compartido con header/categorías/usos/presupuesto (como el mockup: todo filtra el mismo grid).
export function useHomeCatalog() {
  const [state, setState] = useState({ category: "Todos", need: null, budget: "all", brands: new Set(), rams: new Set(), sort: "featured", query: "", offers: false });
  const patch = useCallback((p) => setState((s) => ({ ...s, ...p })), []);
  const reset = useCallback(() => setState({ category: "Todos", need: null, budget: "all", brands: new Set(), rams: new Set(), sort: "featured", query: "", offers: false }), []);
  const go = (p) => { patch(p); setTimeout(scrollToCatalog, 50); };
  return {
    state, patch, reset,
    pickCategory: (c) => go({ category: c, need: null, offers: false }),
    pickNeed: (key) => go({ need: key, category: "Todos", offers: false }),
    pickBudget: (key) => go({ budget: key }),
    pickOffers: () => go({ offers: true, need: null, category: "Todos" }),
    setQuery: (q) => patch({ query: q }),
    showAll: () => { reset(); setTimeout(scrollToCatalog, 50); },
  };
}

function Check({ checked, onChange, children, type = "checkbox", testid }) {
  return (
    <label className={`flex items-center gap-2 text-sm py-1 cursor-pointer rounded px-1 -mx-1 ${checked ? "text-[#002FA7] font-semibold" : "text-zinc-700 hover:text-zinc-900"}`}>
      <input type={type} checked={checked} onChange={onChange} className="accent-[#002FA7] h-4 w-4" data-testid={testid} />
      <span className="truncate">{children}</span>
    </label>
  );
}

// Sección del home "Ofertas y equipos destacados": catálogo con filtros (categoría, precio, marca, RAM).
export default function HomeCatalog({ ctl, budgets = DEFAULT_BUDGETS, needLabels = {}, wishlist, onWish, onAdd, onView }) {
  const { state, patch, reset } = ctl;
  const [products, setProducts] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    axios.get(`${API}/public/storefront?published=1`).then((r) => setProducts(r.data?.products || [])).catch(() => setProducts([]));
  }, []);
  useEffect(() => { setLimit(PAGE); }, [state]);

  const all = useMemo(() => products || [], [products]);
  const categories = useMemo(() => [...new Set(all.map((p) => p.category).filter(Boolean))].sort(), [all]);
  const brandOptions = useMemo(() => [...new Set(all.map((p) => p.brand).filter(Boolean))].sort(), [all]);
  const ramOptions = useMemo(() => [...new Set(all.map(ramGb).filter(Boolean))].sort((a, b) => a - b), [all]);
  const budgetRange = budgets.find((b) => b.key === state.budget);

  const filtered = useMemo(() => {
    const q = norm(state.query.trim());
    const list = all.filter((p) => {
      const price = Number(p.store_price || 0);
      if (state.category !== "Todos" && p.category !== state.category) return false;
      if (state.need && !(p.use_cases || []).includes(state.need)) return false;
      if (state.offers && !(Number(p.compare_at_price || 0) > price)) return false;
      if (budgetRange && (price < (budgetRange.min || 0) || (budgetRange.max != null && price > budgetRange.max))) return false;
      if (state.brands.size && !state.brands.has(p.brand)) return false;
      if (state.rams.size && !state.rams.has(ramGb(p))) return false;
      if (q && !norm(`${p.name} ${p.brand} ${p.category} ${p.short_tagline} ${Object.values(p.specs || {}).join(" ")}`).includes(q)) return false;
      return true;
    });
    if (state.sort === "low") list.sort((a, b) => a.store_price - b.store_price);
    if (state.sort === "high") list.sort((a, b) => b.store_price - a.store_price);
    if (state.sort === "new") list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return list;
  }, [all, state, budgetRange]);

  const active = state.category !== "Todos" || state.budget !== "all" || state.brands.size > 0 || state.rams.size > 0 || !!state.need || state.offers || !!state.query.trim();
  const subtitle = state.need ? `Selección para: ${needLabels[state.need] || state.need}`
    : state.offers ? "Equipos con precio rebajado"
    : state.query.trim() ? `Resultados para "${state.query.trim()}"`
    : state.category !== "Todos" ? `Explorando ${state.category}`
    : "Explora equipos revisados, con garantía y listos para trabajar.";

  const filters = (
    <div className="space-y-5" data-testid="home-catalog-filters">
      <div>
        <h4 className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1.5">Categoría</h4>
        <Check type="radio" checked={state.category === "Todos"} onChange={() => patch({ category: "Todos" })} testid="hc-cat-all">Todos</Check>
        {categories.map((c) => <Check key={c} type="radio" checked={state.category === c} onChange={() => patch({ category: c, need: null })} testid={`hc-cat-${c}`}>{c}</Check>)}
      </div>
      <div>
        <h4 className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1.5">Precio</h4>
        <div className="flex flex-wrap gap-1.5">
          {[{ key: "all", label: "Todos" }, ...budgets].map((b) => (
            <button type="button" key={b.key} onClick={() => patch({ budget: b.key })} className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${state.budget === b.key ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-200 hover:border-[#002FA7]/50"}`} data-testid={`hc-budget-${b.key}`}>{b.label}</button>
          ))}
        </div>
      </div>
      {brandOptions.length > 0 && (
        <div>
          <h4 className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1.5">Marca</h4>
          {brandOptions.map((b) => <Check key={b} checked={state.brands.has(b)} onChange={() => patch({ brands: toggleIn(state.brands, b) })} testid={`hc-brand-${b}`}>{b}</Check>)}
        </div>
      )}
      {ramOptions.length > 0 && (
        <div>
          <h4 className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-1.5">RAM</h4>
          {ramOptions.map((r) => <Check key={r} checked={state.rams.has(r)} onChange={() => patch({ rams: toggleIn(state.rams, r) })} testid={`hc-ram-${r}`}>{r} GB</Check>)}
        </div>
      )}
      {active && <button type="button" onClick={reset} className="text-xs font-semibold text-[#002FA7] hover:underline inline-flex items-center gap-1" data-testid="hc-clear"><X size={11} weight="bold" /> Limpiar filtros</button>}
    </div>
  );

  return (
    <section id="catalogo" className="max-w-7xl mx-auto px-4 sm:px-6 mt-14 scroll-mt-28" data-testid="home-catalog">
      <div className="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono-data uppercase tracking-[0.3em] text-[#E0000F] mb-1 inline-flex items-center gap-1"><Fire size={12} weight="fill" /> Lo más buscado</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Ofertas y equipos destacados</h2>
          <p className="text-sm text-zinc-500 mt-1" data-testid="home-catalog-subtitle">{subtitle}</p>
        </div>
        <Link to="/catalogo" className="text-sm font-semibold text-[#002FA7] inline-flex items-center gap-1 hover:underline" data-testid="home-catalog-all">Catálogo completo <CaretRight size={14} weight="bold" /></Link>
      </div>

      {products === null ? (
        <div className="py-16 text-center text-sm text-zinc-500 inline-flex w-full justify-center items-center gap-2"><Spinner size={16} className="animate-spin" /> Cargando equipos…</div>
      ) : all.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-zinc-300 rounded-2xl" data-testid="home-catalog-empty">
          <Storefront size={36} className="mx-auto text-zinc-300" />
          <h3 className="font-heading font-bold text-lg mt-3">Estamos preparando el catálogo</h3>
          <p className="text-sm text-zinc-500 mt-1">Muy pronto verás aquí nuestros equipos. Escríbenos y te ayudamos a encontrar el ideal.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[230px_minmax(0,1fr)] gap-6 items-start">
          <aside className="hidden lg:block sticky top-28 bg-white border border-zinc-200 rounded-xl p-4">{filters}</aside>
          <div>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setFiltersOpen((v) => !v)} className="lg:hidden inline-flex items-center gap-1.5 text-xs font-semibold border border-zinc-200 rounded-full px-3 py-1.5 bg-white" data-testid="hc-mobile-filters"><FunnelSimple size={13} weight="bold" /> Filtros{active ? " •" : ""}</button>
                <span className="text-sm text-zinc-500" data-testid="home-catalog-count">{filtered.length} {filtered.length === 1 ? "equipo" : "equipos"}</span>
              </div>
              <select value={state.sort} onChange={(e) => patch({ sort: e.target.value })} className="text-sm border border-zinc-200 rounded-full px-3 py-1.5 bg-white" data-testid="home-catalog-sort">
                <option value="featured">Recomendados</option>
                <option value="low">Menor precio</option>
                <option value="high">Mayor precio</option>
                <option value="new">Más nuevos</option>
              </select>
            </div>
            {filtersOpen && <div className="lg:hidden bg-white border border-zinc-200 rounded-xl p-4 mb-4">{filters}</div>}
            {filtered.length === 0 ? (
              <div className="py-16 px-5 text-center border border-dashed border-zinc-300 rounded-2xl" data-testid="home-catalog-noresults">
                <h3 className="font-heading font-bold text-lg">No encontramos equipos con esos filtros</h3>
                <p className="text-sm text-zinc-500 mt-1">Prueba quitar algún filtro o buscar con menos palabras.</p>
                <button type="button" onClick={reset} className="mt-4 bg-[#002FA7] text-white text-sm font-bold px-5 py-2 rounded-full hover:bg-[#0043c2]" data-testid="hc-reset">Ver todo</button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="home-catalog-grid">
                  {filtered.slice(0, limit).map((p) => (
                    <HomeProductCard key={p.id} product={p} className="w-full" testid="hc" wished={wishlist.includes(p.id)} onWish={() => onWish(p.id)} onAdd={() => onAdd(p)} onView={onView ? () => onView(p) : undefined} />
                  ))}
                </div>
                {filtered.length > limit && (
                  <div className="text-center mt-6">
                    <button type="button" onClick={() => setLimit((l) => l + PAGE)} className="text-sm font-semibold text-[#002FA7] border border-[#002FA7]/30 rounded-full px-5 py-2 hover:bg-[#002FA7]/5" data-testid="home-catalog-more">Ver más equipos ({filtered.length - limit})</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
